import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo } from "react";
import { Badge, Button } from "reactstrap";
import { useIntl } from "react-intl";
import { User } from "../../../../cores/session/interfaces/models/user";
import { FreeTrialBadge } from "../../companies/FreeTrialBadge";
import { PlanBadge } from "../../companies/PlanBadge";
import { StatusBadge } from "../../companies/StatusBadge";
import "../../../styles/UsersTable.scss";

interface Props {
  users: User[];
  onEditUser: (user: User) => void;
  selectedIds: string[];
  onToggleUser: (userId: string) => void;
  onToggleAllUsers: () => void;
}

export default function UsersTable({
  users,
  onEditUser,
  selectedIds,
  onToggleUser,
  onToggleAllUsers,
}: Props) {
  const intl = useIntl();
  const allSelected = users.length > 0 && users.every(({ id }) => selectedIds.includes(id));

  const roleLabels = useMemo(
    () => ({
      admin: intl.formatMessage({ id: "app.adminUsers.admin?" }),
      reseller: intl.formatMessage({ id: "app.sidebar.isReseller" }),
      user: intl.formatMessage({ id: "app.adminUsers.user?" }),
    }),
    [intl]
  );

  return (
    <div className="usersTableWrapper">
      <table className="usersTable">
        <thead>
          <tr>
            <th className="usersTable__th usersTable__th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAllUsers}
                aria-label={intl.formatMessage({
                  id: "app.users.bulk.selectAll",
                  defaultMessage: "Seleccionar todos los usuarios visibles",
                })}
              />
            </th>
            <th className="usersTable__th usersTable__th--narrow">ID</th>
            <th className="usersTable__th usersTable__th--name">
              {intl.formatMessage({ id: "app.contacts.name" })}
            </th>
            <th className="usersTable__th">{intl.formatMessage({ id: "app.contacts.email" })}</th>
            <th className="usersTable__th">{intl.formatMessage({ id: "app.contacts.phone" })}</th>
            <th className="usersTable__th">
              {intl.formatMessage({ id: "app.users.tableRole" })}
            </th>
            <th className="usersTable__th">
              {intl.formatMessage({ id: "app.crmBusiness.status" })}
            </th>
            <th className="usersTable__th">
              {intl.formatMessage({ id: "app.users.tablePlan" })}
            </th>
            <th className="usersTable__th usersTable__th--actions">
              {intl.formatMessage({ id: "app.table.actions", defaultMessage: "Acciones" })}
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr
              key={user.id}
              className="usersTable__tr"
              data-selected={selectedIds.includes(user.id) ? "true" : "false"}
            >
              <td className="usersTable__td usersTable__td--checkbox">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(user.id)}
                  onChange={() => onToggleUser(user.id)}
                  aria-label={intl.formatMessage(
                    {
                      id: "app.users.bulk.selectOne",
                      defaultMessage: "Seleccionar usuario {name}",
                    },
                    { name: `${user.lastName} ${user.firstName}`.trim() || user.email }
                  )}
                />
              </td>
              <td className="usersTable__td usersTable__td--muted">#{user.id}</td>
              <td className="usersTable__td usersTable__td--name">
                <div className="usersTable__nameCell">
                  <FontAwesomeIcon icon={["far", "id-card"]} className="usersTable__nameIcon" />
                  <span>{`${user.lastName} ${user.firstName}`}</span>
                </div>
              </td>
              <td className="usersTable__td usersTable__td--ellipsis">{user.email}</td>
              <td className="usersTable__td">{user.phoneNumber || "—"}</td>
              <td className="usersTable__td">
                {user.userRole ? (
                  <div className="usersTable__badges">
                    {(Object.entries(user.userRole) as [string, boolean][]).map(
                      ([role, active]) =>
                        active ? (
                          <Badge pill className="usersTable__roleBadge" key={role}>
                            <span className="text-uppercase small">
                              {(roleLabels as Record<string, string>)[role] ?? role}
                            </span>
                          </Badge>
                        ) : null
                    )}
                  </div>
                ) : (
                  "—"
                )}
              </td>
              <td className="usersTable__td">
                <StatusBadge status={user.status} show />
              </td>
              <td className="usersTable__td">
                <div className="usersTable__planCell">
                  {user.freeTrial && <FreeTrialBadge freeTrial={user.freeTrial} />}
                  {user.userPlan ? (
                    <PlanBadge plan={user.userPlan} show />
                  ) : (
                    <span className="text-muted small">—</span>
                  )}
                </div>
              </td>
              <td className="usersTable__td usersTable__td--actions">
                <Button
                  color="secondary"
                  size="sm"
                  className="usersTable__btn"
                  onClick={() => onEditUser(user)}
                >
                  {intl.formatMessage({ id: "app.adminCompanies.edit" })}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
